
import React, { useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { PriceHistoryChart } from './PriceHistoryChart';
import { publicFeatures } from '../config/publicFeatures';
import { TrendingUp, TrendingDown, Bell, ChevronRight, X } from 'lucide-react';
import { SEOMetadata } from './SEOMetadata';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import TechieScoreBar from './ui/TechieScoreBar';
import { getProductTitle, getTechieScore, getTechieScoreSummary } from '../utils/dataFallbacks';

const ITEMS_PER_PAGE = 12;

// Category mapping to handle SEO page filters to database category_slug mapping
const categoryMapping = {
  'monitors':         'best-computer-monitors',
  'graphics-cards':   'best-graphics-cards-gpu',
  'processors':       'best-processors-cpu',
  'memory':           'fastest-gaming-ram-memory',
  'headsets':         'best-gaming-headsets',
  'headphones':       'best-gaming-headsets', // Add this mapping for headphones
  'mice':             'best-gaming-mice',
  'keyboards':        'best-mechanical-keyboards',
  'laptops':          'best-laptops-for-gaming',
};

const fetchProductsFromSupabase = async ({ pageParam = 1, filters }) => {
  console.log('🔍 ProductList fetchProductsFromSupabase received filters:', filters);

  try {
    const limit = ITEMS_PER_PAGE;
    const offset = (pageParam - 1) * ITEMS_PER_PAGE;

    console.log('🔍 Calling search_products RPC with:', {
      p_search_text: filters.search,
      p_category: filters.category !== 'all' ? filters.category : null,
      p_brands: filters.brands?.length ? filters.brands : null,
      p_min_price: filters.minPrice ? Number(filters.minPrice) : null,
      p_max_price: filters.maxPrice ? Number(filters.maxPrice) : null,
      p_min_rating: filters.rating ? Number(filters.rating) : null,
      p_product_tags: filters.productTags?.length ? filters.productTags : null,
      p_sort: filters.sort || 'featured',
      p_limit: limit,
      p_offset: offset
    });

    const { data, error } = await supabase.rpc('search_products', {
      p_search_text: filters.search || null,
      p_category: filters.category !== 'all' ? filters.category : null,
      p_brands: filters.brands?.length ? filters.brands : null,
      p_min_price: filters.minPrice ? Number(filters.minPrice) : null,
      p_max_price: filters.maxPrice ? Number(filters.maxPrice) : null,
      p_min_rating: filters.rating ? Number(filters.rating) : null,
      p_product_tags: filters.productTags?.length ? filters.productTags : null,
      p_sort: filters.sort || 'featured',
      p_limit: limit,
      p_offset: offset
    });

    if (error) {
      console.error('🔍 RPC query error:', error);
      throw new Error(error.message);
    }

    const totalCount = data?.[0]?.total_count || 0;

    console.log('🔍 RPC query results:', {
      totalCount: totalCount,
      returnedProducts: data?.length || 0,
      sampleTitles: data?.slice(0, 3).map(p => p.title) || []
    });

    return {
      products: data || [],
      nextPage: data && data.length === ITEMS_PER_PAGE ? pageParam + 1 : undefined,
      total: totalCount
    };
    
  } catch (error) {
    console.error('🔍 ProductList fetch error:', error);
    throw error;
  }
};

// suppressSeo: set by editorial guides (DynamicPage), which own title/canonical/robots themselves.
export function ProductList({ filters, view, suppressSeo = false }) {
  // DEBUG: Log filters when ProductList receives them
  //console.log('🔍 ProductList component received filters:', filters);

  const [expandedProduct, setExpandedProduct] = useState(null);
  const { ref, inView } = useInView();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    status
  } = useInfiniteQuery({
    queryKey: ['products', filters],
    queryFn: ({ pageParam }) => fetchProductsFromSupabase({ pageParam, filters }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    keepPreviousData: true,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000 // 10 minutes
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Generate SEO metadata
  const pageTitle = filters.category !== 'all' 
    ? `${filters.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} - Best Deals & Reviews`
    : 'Best Computer Hardware Deals & Reviews';

  const description = filters.category !== 'all'
    ? `Compare prices and find the best deals on ${filters.category.replace(/-/g, ' ')}. Read reviews, check specifications, and make informed decisions.`
    : 'Find the best deals on computer hardware, peripherals, and accessories. Compare prices, read reviews, and make informed buying decisions.';

  const keywords = [
    'computer hardware',
    'tech deals',
    'price comparison',
    'product reviews',
    filters.category !== 'all' ? filters.category.replace(/-/g, ' ') : '',
    'best prices',
    'amazon products'
  ].filter(Boolean);

  const currentPage = data?.pageParams?.length || 1;
  const prevPage = currentPage > 1 ? `?page=${currentPage - 1}` : null;
  const nextPage = hasNextPage ? `?page=${currentPage + 1}` : null;

  if (isLoading) {
    return view === 'grid' ? <ProductGridSkeleton /> : <ProductListSkeleton />;
  }

  if (error) {
    console.error('🔍 ProductList query error:', error);
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Failed to load products: {error.message}</p>
      </div>
    );
  }

  const allProducts = data?.pages.flatMap(page => page.products) || [];

  if (!allProducts.length) {
    console.log('🔍 No products found with current filters:', filters);
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No products found matching your criteria</p>
        <div className="hidden mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
          <p className="text-sm text-yellow-800 font-medium">Debug Info:</p>
          <pre className="text-xs text-yellow-700 mt-2 overflow-x-auto">
            {JSON.stringify(filters, null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <>
      {!suppressSeo && (
        <SEOMetadata
          title={pageTitle}
          description={description}
          keywords={keywords}
          // no canonicalUrl: the category slug is not a route (e.g. /best-computer-monitors is not a page);
          // canonical/robots come from SEORobotsTag, so no og:url is emitted here either.
          prevPage={prevPage}
          nextPage={nextPage}
          products={allProducts}
        />
      )}
      
      <div className={view === 'grid' ? 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4' : 'space-y-4'}>
        <AnimatePresence mode="popLayout">
          {allProducts.map((product, index) => (
            <motion.div
              key={product.asin}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: 0.4, 
                delay: index * 0.05,
                ease: "easeOut"
              }}
              layout
            >
              {view === 'grid' ? (
                <ProductCard product={product} />
              ) : (
                <ProductRow
                  product={product}
                  isExpanded={expandedProduct === product.asin}
                  onToggleExpand={() => setExpandedProduct(
                    expandedProduct === product.asin ? null : product.asin
                  )}
                />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        
        {/* Loading indicator */}
        {isFetchingNextPage && (
          <div className="col-span-full py-4 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          </div>
        )}
        
        {/* Infinite scroll trigger */}
        <div ref={ref} className="h-10 col-span-full" />
        
        {/* Load more button (fallback) */}
        {hasNextPage && !isFetchingNextPage && (
          <div className="col-span-full flex justify-center py-4">
            <button
              onClick={() => fetchNextPage()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow-sm transition-colors"
            >
              Load More Products
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function ProductCard({ product }) {
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  if (!product) return null;

  const {
    brand,
    image_url,
    rating,
    review_count,
    price,
    price_old,
    discount,
    stock_status,
    asin,
    is_prime,
    price_history,
    category_slug
  } = product;

  // Use enriched data with fallbacks
  const title = getProductTitle(product);

  // Get partner tag from environment
  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG;

  // Build Amazon URL with partner tag
  const amazonUrl = `https://www.amazon.com/dp/${asin}?tag=${partnerTag}`;

  const priceChange = price_old ? ((price - price_old) / price_old) * 100 : 0;

  // Check if price history is available
  const hasPriceHistory = publicFeatures.priceHistory && Array.isArray(price_history) && price_history.length > 0;

  return (
    <motion.div 
      layout="position"
      className="bg-white dark:bg-slate-800/90 rounded-xl shadow-lg dark:shadow-slate-900/50 p-6 space-y-6 border border-slate-200 dark:border-slate-700 group transition-all duration-300 relative"
    >
      <Link to={`/dp/${asin}`} className="block">
        <div className="w-full relative aspect-square bg-white dark:bg-white flex items-center justify-center">
          {!isImageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
            </div>
          )}
          {image_url ? (
            <img
              src={image_url}
              alt={`${brand || 'Product'} ${title} - ${category_slug || 'Tech product'} available on Amazon`}
              title={title}
              className={`max-w-full max-h-full w-auto h-auto object-contain p-2 transition-opacity duration-300 ${
                isImageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading="lazy"
              onLoad={() => setIsImageLoaded(true)}
              onError={(e) => {
                e.target.src = '/placeholder-product.png';
                setIsImageLoaded(true);
              }}
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <span className="text-gray-400">No image available</span>
            </div>
          )}
          {discount > 0 && (
            <div className="absolute top-2 right-2">
              <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-sm">
                {Math.round(discount)}% OFF
              </span>
            </div>
          )}
        </div>
      </Link>

      {/* Product Info */}
      <div className="product-info">
        <Link to={`/dp/${asin}`} className="block" title={title}>
          <h3 className="dark:text-white text-xs sm:text-sm font-medium line-clamp-2 hover:text-amazon-link transition-colors mb-2">
            {title}
          </h3>
          {brand && (
            <p className="text-xs text-gray-600 line-clamp-1">{brand}</p>
          )}
        </Link>

        {/* Price */}
        <div className="flex flex-wrap items-baseline gap-1 mb-2">
          <span className="text-base sm:text-lg font-bold text-gray-900">
            ${Number(price).toFixed(2)}
          </span>
          {price_old && (
            <span className="text-xs text-gray-500 line-through">
              ${Number(price_old).toFixed(2)}
            </span>
          )}
          {priceChange !== 0 && (
            <span className={`flex items-center text-xs ${priceChange < 0 ? 'text-green-600' : 'text-red-600'}`}>
              {priceChange < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
              {Math.abs(priceChange).toFixed(1)}%
            </span>
          )}
        </div>

        {/* TechieScore */}
        <div className="mb-3">
          <TechieScoreBar 
            score={getTechieScore(product)} 
            compact={true} 
            showLabel={false}
            summary={getTechieScoreSummary(product)}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <a
            href={amazonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-amazon-yellow hover:bg-amazon-yellow-hover text-amazon-text-primary px-3 py-2 rounded-lg text-sm font-medium text-center transition-colors shadow-sm hover:shadow-md"
          >
            View on Amazon
          </a>
          {publicFeatures.priceHistory && (
          <button
          onClick={() => setShowPriceHistory(true)}
          disabled={!hasPriceHistory}
          className={`w-full px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors
            ${hasPriceHistory 
              ? 'bg-blue-600 text-white' 
              : 'bg-amazon-red text-white opacity-70 cursor-not-allowed'}
          `}
        >
          Price History
        </button>
          )}
        </div>
      </div>

      {/* Price History Modal - Mobile Optimized */}
      <AnimatePresence>
        {showPriceHistory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center p-0 md:p-4 backdrop-blur-sm"
            onClick={() => setShowPriceHistory(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 500 }}
              className="bg-white rounded-t-2xl md:rounded-xl p-4 w-full md:max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-2 border-b">
                <h3 className="text-lg font-semibold">Price History</h3>
                <button
                  onClick={() => setShowPriceHistory(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-2">
                <PriceHistoryChart asin={asin} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ProductRow({ product, isExpanded, onToggleExpand }) {
  if (!product) return null;

  const {
    brand,
    image_url,
    rating,
    review_count,
    price,
    price_old,
    discount,
    stock_status,
    asin,
    is_prime,
    price_history,
    category_slug
  } = product;

  // Use enriched data with fallbacks
  const title = getProductTitle(product);

  // Get partner tag from environment
  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG;

  // Build Amazon URL with partner tag
  const amazonUrl = `https://www.amazon.com/dp/${asin}?tag=${partnerTag}`;

  // Check if price history is available
  const hasPriceHistory = publicFeatures.priceHistory && Array.isArray(price_history) && price_history.length > 0;

  return (
    <motion.div
      layout
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
    >
        <div 
          className="flex flex-col items-center text-center md:text-left md:grid md:grid-cols-[120px,2fr,1fr,1fr,1fr,auto] gap-4 p-4"
          //onClick={onToggleExpand}
        >
          <div className="dark:bg-white w-[120px] h-[120px] flex items-center justify-center rounded-lg bg-white">
            <Link to={`/dp/${asin}`} className="block">
            <img
              src={image_url}
              alt={`${brand || 'Product'} ${title} - ${category_slug || 'Tech product'} with price tracking`}
              title={title}
              className="w-auto h-auto object-contain"
              loading="lazy"
              onError={(e) => {
                e.target.src = '/placeholder-product.png';
              }}
            />
            </Link>
          </div>

          <div>
            <Link to={`/dp/${asin}`} className="block" title={title}>
            <h3 className="dark:text-white font-semibold text-lg line-clamp-2">{title}</h3>
            </Link>
            {brand && (
              <p className="text-gray-600 text-sm">{brand}</p>
            )}
          </div>

          <div>
            <div className="dark:text-white text-xl font-bold">${Number(price).toFixed(2)}</div>
            {price_old && (
              <div className="text-gray-400 line-through text-sm">
                ${Number(price_old).toFixed(2)}
              </div>
            )}
            {discount && (
              <div className="text-green-600 text-sm">
                {Math.round(discount)}% off
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <TechieScoreBar 
              score={getTechieScore(product)} 
              compact={true} 
              showLabel={false}
              summary={getTechieScoreSummary(product)}
            />
          </div>

          <div className="flex gap-2">
            <a
            href={amazonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-amazon-yellow hover:bg-amazon-yellow-hover text-amazon-text-primary px-4 py-2 rounded-lg text-sm font-medium"
            onClick={(e) => e.stopPropagation()}
            >
            View on Amazon
            </a>
            {publicFeatures.priceHistory && (
            <button
            onClick={(e) => {
            e.stopPropagation();
            if (!hasPriceHistory) return;
            onToggleExpand();
            }}
            disabled={!hasPriceHistory}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
            ${hasPriceHistory 
            ? 'bg-blue-600 text-white' 
            : 'bg-amazon-red text-white opacity-70 cursor-not-allowed'}
            `}
            >
            Price History
            </button>
            )}
          </div>
        </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t">
              <PriceHistoryChart asin={asin} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ProductGridSkeleton({ count = 8 }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
      {Array(count).fill(0).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm p-2 sm:p-3">
          <Skeleton height={200} className="mb-3" />
          <Skeleton width={100} className="mb-2" />
          <Skeleton count={2} className="mb-2" />
          <Skeleton width={80} className="mb-2" />
          <Skeleton width={120} className="mb-3" />
          <Skeleton height={35} className="mb-2" />
          <Skeleton height={35} />
        </div>
      ))}
    </div>
  );
}

function ProductListSkeleton({ count = 5 }) {
  return (
    <div className="space-y-4">
      {Array(count).fill(0).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm p-4">
          <div className="grid items-center grid-cols-[120px,2fr,1fr,1fr,1fr,auto] gap-4">
            <Skeleton height={120} width={120} />
            <div>
              <Skeleton count={2} />
            </div>
            <Skeleton width={80} />
            <Skeleton width={100} />
            <Skeleton width={80} />
            <div className="flex gap-2">
              <Skeleton width={120} height={40} />
              <Skeleton width={100} height={40} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
